import React from 'react';
import ReactMarkdown from 'react-markdown';
import { FaSteam, FaSpotify, FaInstagram, FaGithub, FaYoutube, FaTwitter, FaTwitch } from 'react-icons/fa';
import { IoGlobeOutline } from 'react-icons/io5';
import { bannerSrc, isLightPair } from '../utils/avatar';

export const LINK_PLATFORMS = ['website', 'github', 'steam', 'spotify', 'instagram', 'youtube', 'x', 'twitch'];

const ICONS = {
  steam: FaSteam, spotify: FaSpotify, instagram: FaInstagram, github: FaGithub,
  youtube: FaYoutube, x: FaTwitter, twitch: FaTwitch, website: IoGlobeOutline
};

export const linkIcon = (platform) => ICONS[platform] || IoGlobeOutline;

// The banner strip: an image (animated where someone is looking), else the
// chosen plain colour, else the profile colours, else the theme accent.
export function bannerStyle(p, { wide = false, animated = true } = {}) {
  const src = p.banner ? bannerSrc(p.banner, { wide, animated }) : null;
  if (src) return { backgroundImage: `url(${src})` };
  if (p.bannerColor) return { background: p.bannerColor };
  if (p.profileColors) return { background: `linear-gradient(120deg, ${p.profileColors.primary}, ${p.profileColors.accent})` };
  return undefined;
}

// Profile colours tint the whole card. The card's own text uses the theme's
// colour variables, so redefining those here (light or dark set, by the
// pair's brightness) keeps everything inside readable.
export function themedCardProps(p) {
  if (!p.profileColors) return { className: '', style: undefined };
  return {
    className: isLightPair(p.profileColors) ? ' ProfileThemed ProfileThemedLight' : ' ProfileThemed ProfileThemedDark',
    style: { '--profile-primary': p.profileColors.primary, '--profile-accent': p.profileColors.accent }
  };
}

// "Hakkımda": bold, italic, strikethrough, inline code and links only - no
// headings, images or HTML. Links open outside the app (Electron routes
// target=_blank to the system browser).
const ALLOWED = ['p', 'strong', 'em', 'del', 'code', 'a', 'br'];

export function ProfileAbout({ text }) {
  if (!text) return null;
  return (
    <div className="ProfileAbout">
      <ReactMarkdown
        allowedElements={ALLOWED}
        unwrapDisallowed
        components={{
          a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer nofollow">{props.children}</a>
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

const hostLabel = (url) => {
  try {
    const u = new URL(url);
    return (u.hostname.replace(/^www\./, '') + u.pathname).replace(/\/$/, '');
  } catch (e) {
    return url;
  }
};

export function ProfileLinks({ links }) {
  if (!links || links.length === 0) return null;
  return (
    <ul className="ProfileLinks">
      {links.map((l, i) => {
        const Icon = linkIcon(l.platform);
        return (
          <li key={`${l.platform}-${i}`}>
            <a href={l.url} target="_blank" rel="noopener noreferrer nofollow" title={l.url}>
              <Icon />
              <span>{hostLabel(l.url)}</span>
            </a>
          </li>
        );
      })}
    </ul>
  );
}
